/**
 * Location preference persistence using browser localStorage
 * Uses a separate key from cluster preferences to avoid schema conflicts
 */

import type { LocationPreferences } from './types.js';
import { DEFAULT_LOCATION_PREFERENCES } from './types.js';

const LOCATION_STORAGE_KEY = 'le-spoons:location-preferences';

/**
 * Load location preferences from localStorage
 * Falls back to DEFAULT_LOCATION_PREFERENCES if unavailable or invalid
 * @returns LocationPreferences object (never null)
 */
export function loadLocationPreferences(): LocationPreferences {
  try {
    const stored = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!stored) {
      return { ...DEFAULT_LOCATION_PREFERENCES };
    }

    const parsed = JSON.parse(stored) as unknown;

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      (parsed as Record<string, unknown>)['version'] !== 1 ||
      typeof (parsed as Record<string, unknown>)['locationEnabled'] !== 'boolean'
    ) {
      console.warn('Invalid location preferences in localStorage, using defaults');
      return { ...DEFAULT_LOCATION_PREFERENCES };
    }

    return parsed as LocationPreferences;
  } catch {
    console.warn('Failed to load location preferences from localStorage, using defaults');
    return { ...DEFAULT_LOCATION_PREFERENCES };
  }
}

/**
 * Save location preferences to localStorage
 * Gracefully handles localStorage unavailability
 * @param prefs - LocationPreferences to save
 */
export function saveLocationPreferences(prefs: LocationPreferences): void {
  try {
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    console.warn('Failed to save location preferences to localStorage');
  }
}
